import React from 'react';
import PropTypes from 'prop-types';
import { useId } from '../../hooks/useId';
import { TextFieldProps } from './types';
import {
  BaseInput,
  IconContainer,
  StyledLabel,
  TextFieldContainer,
  PrefilledTextContainer,
  StyledPrefilledText,
  sizes,
  TextFieldMainContainer,
} from './TextField.styles';
import { renderFormLabel } from '../renderFormLabel';
import { AdditionalText } from '../AdditionalText';

const _TextField = (
  {
    icon,
    actionButton,
    state = 'default',
    type = 'text',
    fullWidth,
    additionalText,
    prefilledText,
    label,
    disabled,
    id,
    color = 'default',
    size = 'default',
    appearance,
    ariaDescribedby,
    hideLabel = false,
    required,
    ...other
  }: TextFieldProps,
  ref?: React.Ref<HTMLInputElement>
) => {
  const internalId = useId(id);
  const internalDescribedbyId = useId(ariaDescribedby);
  const additionalTextId = other['aria-describedby'] || internalDescribedbyId;

  if (appearance) {
    console.warn(
      'The "appearance" prop is deprecated. It currently does not alter the behaviour of TextField in any way'
    );
  }

  if (state === 'warning') {
    console.warn(
      'The "warning" state is deprecated. It currently does not alter the behaviour of TextField in any way'
    );
  }
  const needsAutocompleteAttribute = !disabled;
  if (needsAutocompleteAttribute && !other.autoComplete) {
    console.warn(
      'A11y issue: You have not provided the `autocomplete` attribute for a TextField element',
      '`autoComplete` is a hint for form autofill feature, see the list on https://html.spec.whatwg.org/#autofill-detail-tokens for more info on how the value should look like'
    );
  }

  if (!label) {
    console.warn(
      'A11y issue: You are not using the `label` for the TextField element. Hence, the screen readers are not be able to provide enough context.',
      'You should provide a label and use hideLabel prop if you do not need show it'
    );
  }

  const styledLabel = (label: string) => (
    <StyledLabel
      state={state}
      htmlFor={id || internalId}
      color={color}
      size={size}
      disabled={disabled}
      readOnly={other.readOnly}
    >
      {required ? `*${label}` : label}
    </StyledLabel>
  );

  let actionButtonContent: React.ReactElement | undefined = actionButton;
  let ActionButtonElement;

  if (actionButton) {
    actionButtonContent = React.cloneElement(actionButton, { disabled: disabled });
    ActionButtonElement = (
      <IconContainer state={state} position="right" color={color} size={size} disabled={disabled}>
        {actionButtonContent}
      </IconContainer>
    );
  }

  return (
    <TextFieldMainContainer gap={sizes[size].containerGap} fullWidth={fullWidth}>
      {renderFormLabel(styledLabel, label, hideLabel)}
      <TextFieldContainer>
        <BaseInput
          ref={ref}
          fullWidth={fullWidth}
          type={type}
          state={state}
          aria-invalid={state === 'error'}
          aria-describedby={additionalText ? additionalTextId : undefined}
          disabled={disabled}
          {...other}
          id={id || internalId}
          hasIcon={Boolean(icon)}
          hasActionButton={Boolean(actionButton)}
          hasPrefilledText={Boolean(prefilledText)}
          color={color}
          textFieldSize={size}
          aria-readonly={other.readOnly}
          required={required}
          aria-required={required}
        />
        {icon && (
          <IconContainer state={state} color={color} size={size} readOnly={other.readOnly}>
            {icon}
          </IconContainer>
        )}
        {prefilledText && (
          <PrefilledTextContainer hasActionButton={Boolean(actionButton)} size={size}>
            <StyledPrefilledText
              id={`prefilled-${internalId}`}
              aria-live="polite"
              color={color}
              textFieldSize={size}
            >
              {prefilledText}
            </StyledPrefilledText>
          </PrefilledTextContainer>
        )}
        {actionButton && ActionButtonElement}
      </TextFieldContainer>
      {additionalText && (
        <AdditionalText
          size={size}
          state={state}
          color={color}
          additionalTextId={additionalTextId}
          additionalText={additionalText}
          disabled={disabled}
          readOnly={other.readOnly}
        />
      )}
    </TextFieldMainContainer>
  );
};

export const TextField = React.forwardRef(_TextField);

TextField.propTypes = {
  state: PropTypes.oneOf(['default', 'error', 'success', 'warning']),
  color: PropTypes.oneOf(['default', 'lightened'] as const),
  size: PropTypes.oneOf(['default', 'large'] as const),
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  icon: PropTypes.element,
  placeholder: PropTypes.string,
  additionalText: PropTypes.string,
  prefilledText: PropTypes.string,
  label: PropTypes.string,
  actionButton: PropTypes.element,
  type: PropTypes.oneOf(['text', 'password', 'email', 'number', 'url', 'search', 'date'] as const),
  hideLabel: PropTypes.bool,
  required: PropTypes.bool,
};
